import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  Calendar,
  Clock,
  Plus,
  Eye,
  Trash2,
  Filter,
  X,
  User,
  Laptop,
  Users,
  CheckCircle,
  Circle,
  PlayCircle,
  XCircle,
} from 'lucide-react';
import api from '../services/api';
import type { SessionWithPatient } from '../../../shared/session-types';

const statusConfig = {
  pendiente: { label: 'Pendiente', icon: Circle, color: 'text-yellow-400', bgColor: 'bg-yellow-500/20' },
  en_curso: { label: 'En Curso', icon: PlayCircle, color: 'text-blue-400', bgColor: 'bg-blue-500/20' },
  finalizada: { label: 'Finalizada', icon: CheckCircle, color: 'text-green-400', bgColor: 'bg-green-500/20' },
  cancelada: { label: 'Cancelada', icon: XCircle, color: 'text-red-400', bgColor: 'bg-red-500/20' },
};

export default function ProfessionalSessionsPage() {
  const [sessions, setSessions] = useState<SessionWithPatient[]>([]);
  const [loading, setLoading] = useState(true);
  const [filterEstado, setFilterEstado] = useState<string>('');
  const [filterPaciente, setFilterPaciente] = useState<string>('');

  useEffect(() => {
    loadSessions();
  }, [filterEstado]);

  const loadSessions = async () => {
    try {
      setLoading(true);
      const params: any = {};
      if (filterEstado) params.estado = filterEstado;

      const response = await api.get('/professional/sessions', { params });

      if (response.data.ok) {
        setSessions(response.data.data);
      }
    } catch (error) {
      console.error('Error al cargar sesiones:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (session: SessionWithPatient) => {
    if (
      !confirm(
        `¿Eliminar sesión para ${session.paciente_nombre} ${session.paciente_apellidos}?\n\nEsta acción NO se puede deshacer.`
      )
    ) {
      return;
    }

    try {
      const response = await api.delete(`/professional/sessions/${session.id}`);
      if (response.data.ok) {
        loadSessions();
      }
    } catch (error) {
      console.error('Error al eliminar sesión:', error);
      alert('Error al eliminar la sesión');
    }
  };

  const formatDate = (date: Date | string) => {
    return new Date(date).toLocaleDateString('es-ES', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const filteredSessions = sessions.filter((session) => {
    if (filterPaciente) {
      const searchTerm = filterPaciente.toLowerCase();
      return (
        session.paciente_nombre.toLowerCase().includes(searchTerm) ||
        session.paciente_apellidos.toLowerCase().includes(searchTerm) ||
        session.paciente_username.toLowerCase().includes(searchTerm)
      );
    }
    return true;
  });

  return (
    <div className="min-h-screen py-8 px-4">
      <div className="container mx-auto max-w-7xl">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="mb-8 flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4"
        >
          <div>
            <h1 className="text-4xl font-display font-bold mb-2">Sesiones</h1>
            <p className="text-white/70">
              {filteredSessions.length} {filteredSessions.length === 1 ? 'sesión' : 'sesiones'}
            </p>
          </div>
          <Link to="/profesional/sesiones/nueva" className="btn-neureka">
            <Plus className="w-4 h-4" />
            Nueva Sesión
          </Link>
        </motion.div>

        {/* Filters */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="glass-container mb-6"
        >
          <div className="flex items-center gap-3 mb-4">
            <Filter className="w-5 h-5 text-neureka-yellow" />
            <h2 className="text-lg font-semibold">Filtros</h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {/* Filtro por Estado */}
            <div>
              <label className="block text-sm font-medium mb-2">Estado</label>
              <div className="flex gap-2 flex-wrap">
                <button
                  onClick={() => setFilterEstado('')}
                  className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${
                    filterEstado === ''
                      ? 'bg-neureka-yellow text-neureka-purple'
                      : 'bg-white/10 text-white/70 hover:bg-white/20'
                  }`}
                >
                  Todos
                </button>
                {Object.entries(statusConfig).map(([key, config]) => (
                  <button
                    key={key}
                    onClick={() => setFilterEstado(key)}
                    className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors flex items-center gap-2 ${
                      filterEstado === key
                        ? `${config.bgColor} ${config.color}`
                        : 'bg-white/10 text-white/70 hover:bg-white/20'
                    }`}
                  >
                    <config.icon className="w-3.5 h-3.5" />
                    {config.label}
                  </button>
                ))}
              </div>
            </div>

            {/* Filtro por Paciente */}
            <div>
              <label className="block text-sm font-medium mb-2">Buscar Paciente</label>
              <div className="relative">
                <input
                  type="text"
                  value={filterPaciente}
                  onChange={(e) => setFilterPaciente(e.target.value)}
                  placeholder="Nombre, apellidos o username..."
                  className="input-neureka w-full pr-10"
                />
                {filterPaciente && (
                  <button
                    onClick={() => setFilterPaciente('')}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-white/50 hover:text-white/70"
                  >
                    <X className="w-4 h-4" />
                  </button>
                )}
              </div>
            </div>
          </div>
        </motion.div>

        {/* Loading */}
        {loading ? (
          <div className="text-center py-12">
            <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-neureka-yellow"></div>
            <p className="mt-4 text-white/70">Cargando sesiones...</p>
          </div>
        ) : filteredSessions.length === 0 ? (
          /* Empty State */
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="glass-container text-center py-12"
          >
            <Calendar className="w-16 h-16 text-white/30 mx-auto mb-4" />
            <h3 className="text-xl font-semibold mb-2">
              {filterEstado || filterPaciente ? 'No se encontraron sesiones' : 'No tienes sesiones aún'}
            </h3>
            <p className="text-white/60 mb-6">
              {filterEstado || filterPaciente
                ? 'Intenta cambiar los filtros'
                : 'Crea tu primera sesión para comenzar'}
            </p>
            {!filterEstado && !filterPaciente && (
              <Link to="/profesional/sesiones/nueva" className="btn-neureka">
                <Plus className="w-4 h-4" />
                Crear Primera Sesión
              </Link>
            )}
          </motion.div>
        ) : (
          /* Sessions Grid */
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {filteredSessions.map((session, index) => {
              const config = statusConfig[session.estado];
              return (
                <motion.div
                  key={session.id}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.05 }}
                  className="glass-container hover:scale-105 transition-transform"
                >
                  {/* Header */}
                  <div className="flex items-start justify-between mb-4">
                    <div className="flex items-center gap-3">
                      {session.paciente_avatar ? (
                        <img
                          src={session.paciente_avatar}
                          alt={session.paciente_nombre}
                          className="w-12 h-12 rounded-full"
                        />
                      ) : (
                        <div className="w-12 h-12 rounded-full bg-purple-500/20 flex items-center justify-center">
                          <span className="text-purple-300 font-semibold">
                            {session.paciente_nombre.charAt(0)}
                            {session.paciente_apellidos.charAt(0)}
                          </span>
                        </div>
                      )}
                      <div>
                        <h3 className="font-semibold">
                          {session.paciente_nombre} {session.paciente_apellidos}
                        </h3>
                        <p className="text-sm text-white/50">@{session.paciente_username}</p>
                      </div>
                    </div>
                  </div>

                  {/* Status */}
                  <div className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-lg ${config.bgColor} mb-4`}>
                    <config.icon className={`w-4 h-4 ${config.color}`} />
                    <span className={`text-sm font-medium ${config.color}`}>{config.label}</span>
                  </div>

                  {/* Info */}
                  <div className="space-y-2 mb-4">
                    <div className="flex items-center gap-2 text-sm text-white/70">
                      {session.tipo_sesion === 'remota' ? (
                        <Laptop className="w-4 h-4" />
                      ) : (
                        <Users className="w-4 h-4" />
                      )}
                      <span className="capitalize">{session.tipo_sesion}</span>
                    </div>
                    <div className="flex items-center gap-2 text-sm text-white/70">
                      <Calendar className="w-4 h-4" />
                      {formatDate(session.fecha_creacion)}
                    </div>
                    {session.duracion_minutos && (
                      <div className="flex items-center gap-2 text-sm text-white/70">
                        <Clock className="w-4 h-4" />
                        {session.duracion_minutos} minutos
                      </div>
                    )}
                  </div>

                  {/* Progress */}
                  <div className="mb-4">
                    <div className="flex justify-between text-sm mb-2">
                      <span className="text-white/70">Progreso</span>
                      <span className="font-semibold">
                        {session.juegos_completados}/{session.total_juegos} juegos
                      </span>
                    </div>
                    <div className="h-2 bg-white/10 rounded-full overflow-hidden">
                      <div
                        className="h-full bg-neureka-yellow rounded-full transition-all"
                        style={{
                          width: `${session.total_juegos > 0 ? (session.juegos_completados / session.total_juegos) * 100 : 0}%`,
                        }}
                      />
                    </div>
                  </div>

                  {/* Actions */}
                  <div className="flex gap-2">
                    <Link
                      to={`/profesional/sesiones/${session.id}`}
                      className="flex-1 btn-neureka-secondary text-sm"
                    >
                      <Eye className="w-4 h-4" />
                      Ver Detalles
                    </Link>
                    <button
                      onClick={() => handleDelete(session)}
                      className="p-2 rounded-lg hover:bg-red-500/20 text-red-400 transition-colors"
                      title="Eliminar"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                </motion.div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
